import React, { useState, useCallback, useMemo, useEffect } from "react";
import { MdClose, MdShoppingCart } from "react-icons/md";
import { FaPepperHot } from "react-icons/fa";
import { MenuItem } from "../redux/slices/menuSlice";
import {
  filterEnabledModifiers,
  handleModifierOptionSelection,
  isModifierOptionSelected,
  isModifierRequired,
  isModifierMultiSelect,
} from "../utils/modifierUtils";
import { calculateTotalPrice, formatPrice } from "../utils/priceUtils";
import {
  shouldShowSpiceLevel,
  SPICE_LEVELS,
  DEFAULT_SPICE_LEVEL,
} from "../utils/spiceUtils";
import {
  validateAllSelections,
  type ValidationErrors,
} from "../utils/validationUtils";

interface ModifierOption {
  id: string | number;
  name: string;
  price: number | string;
}

interface SelectedModifier {
  name: string;
  options: ModifierOption[];
}

interface AddToCartModalProps {
  isOpen: boolean;
  item: MenuItem | null;
  onClose: () => void;
  onAddToCart: (
    item: MenuItem,
    quantity: number,
    spiceLevel: number | null,
    modifiers: SelectedModifier[]
  ) => Promise<void> | void;
  editMode?: boolean;
  existingCartItem?: any;
  onUpdateCart?: (
    cartItemId: string,
    item: MenuItem,
    quantity: number,
    spiceLevel: number | null,
    modifiers: SelectedModifier[]
  ) => Promise<void> | void;
}

export const AddToCartModal: React.FC<AddToCartModalProps> = React.memo(
  ({ isOpen, item, onClose, onAddToCart, editMode = false, existingCartItem, onUpdateCart }) => {
    const [quantity, setQuantity] = useState(1);
    const [spiceLevel, setSpiceLevel] = useState(DEFAULT_SPICE_LEVEL);
    const [selectedModifiers, setSelectedModifiers] = useState<SelectedModifier[]>([]);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Reset when modal opens or pre-populate for edit mode
    useEffect(() => {
      if (isOpen && item) {
        if (editMode && existingCartItem) {
          // Pre-populate with existing cart item data
          setQuantity(existingCartItem.quantity);
          setSpiceLevel(existingCartItem.selected.spice?.id || DEFAULT_SPICE_LEVEL);
          setSelectedModifiers(existingCartItem.selected.modifiers || []);
        } else {
          // Reset for new item
          setQuantity(1);
          setSpiceLevel(DEFAULT_SPICE_LEVEL);
          setSelectedModifiers([]);
        }
        setValidationErrors({});
        setIsAddingToCart(false);
        setError(null);
      }
    }, [isOpen, item, editMode, existingCartItem]);

    // ESC key close
    useEffect(() => {
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.key === "Escape" && isOpen) {
          onClose();
        }
      };
      if (isOpen) {
        document.addEventListener("keydown", handleEscKey);
        return () => document.removeEventListener("keydown", handleEscKey);
      }
    }, [isOpen, onClose]);

    // Modifiers list
    const modifiersList = useMemo(
      () => (item ? filterEnabledModifiers(item.modifiers_list || []) : []),
      [item?.modifiers_list]
    );

    const handleModifierOptionSelect = useCallback(
      (modifierName: string, option: ModifierOption) => {
        setSelectedModifiers((prev) =>
          handleModifierOptionSelection(modifierName, option, prev, modifiersList)
        );
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[modifierName];
          return newErrors;
        });
      },
      [modifiersList]
    );

    const isModifierOptionSelectedLocal = useCallback(
      (modifierName: string, optionName: string): boolean =>
        isModifierOptionSelected(modifierName, optionName, selectedModifiers),
      [selectedModifiers]
    );

    const totalPrice = useMemo(
      () =>
        item ? calculateTotalPrice(item.price, selectedModifiers, quantity) : 0,
      [item, selectedModifiers, quantity]
    );

    const validateSelections = useCallback((): boolean => {
      if (!item) return false;
      const validation = validateAllSelections(
        item,
        modifiersList,
        selectedModifiers,
        spiceLevel
      );
      setValidationErrors(validation.errors);
      return validation.isValid;
    }, [item, modifiersList, selectedModifiers, spiceLevel]);

    const handleAddToCartClick = useCallback(async () => {
      if (!item || !validateSelections()) return;

      setIsAddingToCart(true);
      setError(null);

      try {
        const finalSpiceLevel = shouldShowSpiceLevel(item) ? spiceLevel : null;
        
        if (editMode && existingCartItem && onUpdateCart) {
          await onUpdateCart(existingCartItem.cartItemId, item, quantity, finalSpiceLevel, selectedModifiers);
        } else {
          await onAddToCart(item, quantity, finalSpiceLevel, selectedModifiers);
        }
        onClose();
      } catch (err) {
        console.error(editMode ? "Update order failed:" : "Add to order failed:", err);
        setError(editMode ? "Failed to update order item. Please try again." : "Failed to add item to order. Please try again.");
      } finally {
        setIsAddingToCart(false);
      }
    }, [
      item,
      quantity,
      spiceLevel,
      selectedModifiers,
      onAddToCart,
      onUpdateCart,
      editMode,
      existingCartItem,
      onClose,
      validateSelections,
    ]);

    const isFormValid = useMemo(() => {
      if (!item) return false;
      return validateAllSelections(
        item,
        modifiersList,
        selectedModifiers,
        spiceLevel
      ).isValid;
    }, [item, modifiersList, selectedModifiers, spiceLevel]);

    if (!isOpen || !item) return null;

    return (
      <div
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md md:max-w-lg lg:max-w-xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-200 p-4 flex items-center justify-between">
            <h2
              id="modal-title"
              className="text-lg sm:text-xl font-bold text-gray-900"
            >
              {item.name}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <MdClose className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-6">
            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Spice Level */}
            {shouldShowSpiceLevel(item) && (
              <section>
                <h4 className="font-semibold mb-3 text-sm sm:text-base">
                  Spice Level
                  <span className="text-red-500 ml-1">*</span>
                </h4>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {SPICE_LEVELS.map((level) => (
                    <button
                      key={level.id}
                      onClick={() => setSpiceLevel(level.id)}
                      className={`p-2 sm:p-3 rounded-lg border text-center transition-colors ${
                        spiceLevel === level.id
                          ? "border-red-500 bg-red-200"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      aria-pressed={spiceLevel === level.id}
                    >
                      <div className="flex items-center justify-center">
                        {Array.from({ length: level.pepperCount }).map((_, i) => (
                          <FaPepperHot
                            key={i}
                            className={`h-4 w-4 sm:h-5 sm:w-5 mx-0.5 ${
                              spiceLevel === level.id ? "text-red-700" : "text-red-600"
                            }`}
                          />
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
                {validationErrors["Spice Level"] && (
                  <p
                    className="text-red-500 text-xs mt-1 font-medium"
                    role="alert"
                  >
                    {validationErrors["Spice Level"]}
                  </p>
                )}
              </section>
            )}

            {/* Modifiers */}
            {modifiersList.map((modifier, index) => (
              <section key={index}>
                <h4 className="font-semibold mb-3 text-sm sm:text-base">
                  {modifier.name}
                  {isModifierRequired(modifier) && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </h4>
                <div
                  className="space-y-2"
                  role="group"
                  aria-labelledby={`modifier-${index}`}
                >
                  {modifier.options.map((option: ModifierOption, optIndex: number) => (
                    <button
                      key={optIndex}
                      onClick={() => handleModifierOptionSelect(modifier.name, option)}
                      className={`w-full p-2 sm:p-3 rounded-lg border text-left transition-colors flex items-center justify-between ${
                        isModifierOptionSelectedLocal(modifier.name, option.name)
                          ? "border-red-500 bg-red-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      aria-pressed={isModifierOptionSelectedLocal(
                        modifier.name,
                        option.name
                      )}
                    >
                      <div className="flex items-center">
                        <div className="mr-3">
                          {isModifierMultiSelect(modifier) ? (
                            <div
                              className={`w-4 h-4 border rounded ${
                                isModifierOptionSelectedLocal(modifier.name, option.name)
                                  ? "bg-red-500 border-red-500"
                                  : "border-gray-400"
                              } flex items-center justify-center`}
                            >
                              {isModifierOptionSelectedLocal(modifier.name, option.name) && (
                                <svg
                                  className="w-3 h-3 text-white"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                          ) : (
                            <div
                              className={`w-4 h-4 border rounded-full ${
                                isModifierOptionSelectedLocal(modifier.name, option.name)
                                  ? "border-red-500"
                                  : "border-gray-400"
                              } flex items-center justify-center`}
                            >
                              {isModifierOptionSelectedLocal(modifier.name, option.name) && (
                                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                              )}
                            </div>
                          )}
                        </div>
                        <span className="font-medium text-xs sm:text-sm">
                          {option.name}
                        </span>
                      </div>
                      <span className="text-xs text-gray-600 font-medium">
                        +{formatPrice(option.price)}
                      </span>
                    </button>
                  ))}
                </div>
                {validationErrors[modifier.name] && (
                  <p
                    className="text-red-500 text-xs mt-1 font-medium"
                    role="alert"
                  >
                    {validationErrors[modifier.name]}
                  </p>
                )}
              </section>
            ))}

            {/* Add to Cart */}
            <div className="border-t border-gray-200 pt-4">
              <button
                onClick={handleAddToCartClick}
                disabled={isAddingToCart || !isFormValid}
                className={`w-full font-semibold py-3 rounded-xl transition-all duration-200 flex items-center justify-center ${
                  isAddingToCart || !isFormValid
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-red-500 hover:bg-red-600 text-white"
                }`}
                aria-label={`Add ${item.name} to order for ${formatPrice(totalPrice)}`}
              >
                {isAddingToCart ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    {editMode ? "Updating..." : "Adding..."}
                  </div>
                ) : (
                  <>
                    <MdShoppingCart className="w-4 h-4 mr-2" />
                    {editMode ? `Update Order • ${formatPrice(totalPrice)}` : `Add to Order • ${formatPrice(totalPrice)}`}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

AddToCartModal.displayName = 'AddToCartModal';
